
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    MdArrowBack,
    MdSend,
    MdDelete,
    MdEdit,
    MdSave
} from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './AddGenerateRequest.css';
import './AddProject.css';
import Swal from 'sweetalert2';
import axios from 'axios';
import {
    UPDATE_PURCHASE_ORDER_API,
    VIEW_PURCHASE_ORDER_API,
    GET_PROJECT_VENDORS_API,
    GET_ALL_PROJECT_API,
    GET_ALL_EMPLOYEES_API,
    GET_ALL_PRODUCTS_API,
    GET_ALL_UNITS_API,
    SEND_WHATSAPP_LINK_API,
    VENDOR_PORTAL_URL
} from '../config/API';
import { encryptToken } from '../utils/secureToken';
import Loader from '../components/common/Loader';
import Modal from '../components/common/Modal'; // Import the custom Modal
import PaginatedSelect from '../components/common/PaginatedSelect';
import { showErrorToast } from '../utils/toastUtils';

const EditGenerateRequest = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [dropdownCacheUniq, setDropdownCacheUniq] = useState(Date.now());
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [originalRequestData, setOriginalRequestData] = useState(null);
    const [loadingText, setLoadingText] = useState('Updating Generate Request...');

    // --- WhatsApp Notification State ---
    const [whatsappLoading, setWhatsappLoading] = useState(false);
    const [whatsappProgress, setWhatsappProgress] = useState({ current: 0, total: 0, vendorName: '' });

    // --- Item Modal State ---
    const [itemModalOpen, setItemModalOpen] = useState(false);
    const [itemModalData, setItemModalData] = useState({
        productId: '',
        productName: '',
        productDescription: '',
        unit: null,
        size: '',
        qty: 1,
        rate: 0,
        total: 0
    });
    const [isEditingItem, setIsEditingItem] = useState(false);

    const [formData, setFormData] = useState({
        vendorId: [],
        projectId: null,
        employeeId: null,
        date: new Date(),
        tillDate: null,
        particular: '',
        status: 'pending'
    });

    const [items, setItems] = useState([]);
    const [grandTotal, setGrandTotal] = useState(0);
    const [vendorOptions, setVendorOptions] = useState([]);

    useEffect(() => {
        const fetchPOData = async () => {
            try {
                setFetchingData(true);
                const token = localStorage.getItem('accessToken');
                const headers = { Authorization: `Bearer ${token}` };

                // Fetch Existing PO Data
                const poRes = await axios.get(VIEW_PURCHASE_ORDER_API(id), { headers });

                if (poRes.data.status) {
                    const po = poRes.data.data;
                    setOriginalRequestData(po);

                    const requestVendors = po.RequestVendors || po.PurchaseOrderVendors || [];
                    const requestItems = po.RequestItems || po.PurchaseOrderItems || [];

                    // 1. Map Vendors
                    let mappedVendors = [];
                    if (requestVendors.length > 0) {
                        const uniqueVendors = [...new Map(requestVendors.map(rv => [rv.Vendor?.id || rv.Vendor?._id, rv])).values()];
                        mappedVendors = uniqueVendors.map(rv => {
                            const v = rv.Vendor || {};
                            return {
                                value: v.id || v._id,
                                label: v.vendorName + " (" + v.companyName + ")",
                                whatsappNumber: v.whatsAppNumber || v.whatsappNumber || v.whatsapp_number || v.contactNumber || v.mobileNumber || ''
                            };
                        });
                    } else if (po.vendorId) {
                        mappedVendors = [{
                            value: po.vendorId,
                            label: po.Vendor?.vendorName || po.vendorName || 'Selected Vendor',
                            whatsappNumber: po.Vendor?.whatsAppNumber || po.Vendor?.whatsappNumber || po.Vendor?.whatsapp_number || po.Vendor?.contactNumber || po.Vendor?.mobileNumber || po.whatsappNumber || ''
                        }];
                    }

                    const projectId = po.projectId || po.ProjectMaster?.id || po.Project?.id || po.ProjectMaster?._id || po.Project?._id;
                    const employeeId = po.employeeId || po.Employee?.id || po.Employee?._id;

                    setFormData({
                        vendorId: mappedVendors,
                        projectId: {
                            value: projectId,
                            label: po.projectName || po.ProjectMaster?.projectName || po.Project?.projectName || 'Selected Project'
                        },
                        employeeId: {
                            value: employeeId,
                            label: (po.firstName && po.lastName) ? `${po.firstName} ${po.lastName}` : (`${po.Employee?.firstName || ''} ${po.Employee?.lastName || ''}`.trim() || po.Employee?.fullName || 'Selected Employee')
                        },
                        date: new Date(po.date),
                        tillDate: po.tillDate ? new Date(po.tillDate) : null,
                        particular: po.particular || '',
                        status: po.status || 'pending'
                    });

                    // 2. Map Items
                    if (Array.isArray(requestItems)) {
                        const loadedItems = requestItems.map((item, index) => {
                            let reqVendorId = item.reqVendorId;
                            if (!reqVendorId && requestVendors.length > 0) {
                                const rv = requestVendors.find(v => v.vendorId === item.vendorId) || requestVendors[0];
                                reqVendorId = rv?.id || rv?._id;
                            }

                            return {
                                id: Date.now() + index,
                                srNo: index + 1,
                                reqItemId: item.reqItemId || item.id || item._id,
                                reqVendorId: reqVendorId,
                                productId: item.productId || item.Product?.id || item.Product?._id,
                                productName: item.Product?.productName || item.productName || item.productDescription,
                                productDescription: item.productDescription,
                                unitId: item.unitId || item.Unit?.id || item.Product?.Unit?.id || item.Product?.Unit?._id || '',
                                unitName: item.Unit?.unitName || item.unit || item.Product?.Unit?.unitName || '',
                                size: item.size || '',
                                qty: item.qty,
                                rate: parseFloat(item.rate) || 0,
                                total: parseFloat(item.total) || 0
                            };
                        });
                        setItems(loadedItems);
                    }
                }
            } catch (error) {
                console.error('Error fetching PO details:', error);
            } finally {
                setFetchingData(false);
            }
        };

        if (id) fetchPOData();
    }, [id]);

    useEffect(() => {
        const total = items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
        setGrandTotal(total);
    }, [items]);


    const fetchProjectVendors = async () => {
        if (!formData.projectId?.value) {
            setVendorOptions([]);
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_PROJECT_VENDORS_API(formData.projectId.value), {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                const { assignedVendors = [], unassignedVendors = [] } = response.data.data || {};
                const mapVendor = (v) => ({
                    value: v.id || v._id,
                    label: v.companyName ? `${v.vendorName} (${v.companyName})` : v.vendorName,
                    whatsappNumber: v.whatsAppNumber || v.whatsappNumber || v.whatsapp_number || v.contactNumber || v.mobileNumber || ''
                });

                const groupedOptions = [];
                if (assignedVendors.length > 0) {
                    groupedOptions.push({
                        label: 'Assigned Vendors',
                        options: assignedVendors.map(mapVendor)
                    });
                }
                if (unassignedVendors.length > 0) {
                    groupedOptions.push({
                        label: 'All Other Vendors',
                        options: unassignedVendors.map(mapVendor)
                    });
                }

                // Set the vendor options
                setVendorOptions(groupedOptions.length > 0 ? groupedOptions : []);
            }
        } catch (error) {
            console.error('Error fetching project vendors:', error);
            setVendorOptions([]);
        }
    };

    useEffect(() => {
        fetchProjectVendors();
    }, [formData.projectId?.value]); // This will trigger when project ID changes



    const handleSelectChange = (selected, field) => {
        setFormData(prev => ({
            ...prev,
            [field]: selected
        }));

        // If project changes, clear vendors
        if (field === 'projectId') {
            setFormData(prev => ({
                ...prev,
                vendorId: [] // Clear vendors when project changes
            }));
        }
    };

    const handleDateChange = (date) => {
        setFormData(prev => ({ ...prev, date: date }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleItemChange = (itemId, field, value) => {
        setItems(prev => prev.map(item => {
            if (item.id === itemId) {
                const updatedItem = { ...item, [field]: value };
                if (field === 'qty' || field === 'rate') {
                    updatedItem.total = (parseFloat(updatedItem.qty) || 0) * (parseFloat(updatedItem.rate) || 0);
                }
                return updatedItem;
            }
            return item;
        }));
    };

    const removeItemRow = (itemId) => {
        if (items.length === 0) return;
        const filtered = items.filter(item => item.id !== itemId);
        const reOrdered = filtered.map((item, index) => ({ ...item, srNo: index + 1 }));
        setItems(reOrdered);
    };

    const openItemModal = (itemData, isEdit = false) => {
        setIsEditingItem(isEdit);
        setItemModalData({
            ...itemData,
            unit: itemData.unitId ? { value: itemData.unitId, label: itemData.unitName || itemData.unit || 'Select Unit' } :
                (itemData.unit ? { value: itemData.unit, label: itemData.unit } : null)
        });
        setItemModalOpen(true);
    };

    const handleItemModalChange = (field, value) => {
        setItemModalData(prev => {
            const updates = { [field]: value };
            if (field === 'qty' || field === 'rate') {
                const q = field === 'qty' ? parseFloat(value) || 0 : parseFloat(prev.qty) || 0;
                const r = field === 'rate' ? parseFloat(value) || 0 : parseFloat(prev.rate) || 0;
                updates.total = (q * r).toFixed(2);
            }
            return { ...prev, ...updates };
        });
    };

    const handleSaveItem = () => {

        if (itemModalData.qty <= 0) {
            Swal.fire('Error', 'Quantity must be greater than 0', 'error');
            return;
        }

        if (isEditingItem) {
            setItems(prev => prev.map(it => it.id === itemModalData.id ? {
                ...it,
                ...itemModalData,
                unitId: itemModalData.unit ? itemModalData.unit.value : '',
                unitName: itemModalData.unit ? itemModalData.unit.label : ''
            } : it));
        } else {
            const newItem = {
                ...itemModalData,
                id: Date.now(),
                srNo: items.length + 1,
                unitId: itemModalData.unit ? itemModalData.unit.value : '',
                unitName: itemModalData.unit ? itemModalData.unit.label : ''
            };
            setItems(prev => [...prev, newItem]);
        }
        setItemModalOpen(false);
    };

    const handleItemSelect = (selected) => {
        if (!selected) return;

        openItemModal({
            productId: selected.value,
            productName: selected.label,
            productDescription: selected.description || selected.label,
            unitId: selected.unitId || '',
            unitName: selected.unitName || '',
            size: selected.size || '',
            qty: 1,
            rate: selected.rate || 0,
            total: selected.rate || 0
        });
    };

    const handleEditItem = (item) => {
        openItemModal(item, true);
    };

    const handleUpdate = async (isDraft = false) => {


        const result = await Swal.fire({
            title: isDraft ? 'Save as Draft?' : 'Update Generate Request?',
            text: isDraft ? 'Are you sure you want to save this as a draft?' : 'Are you sure you want to update this Generate Request?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: isDraft ? 'Yes, Save Draft' : 'Yes, Update',
            cancelButtonText: 'Cancel',
            confirmButtonColor: isDraft ? '#64748b' : '#10b981',
            customClass: { confirmButton: 'swal-confirm-btn', cancelButton: 'swal-cancel-btn' },
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                setLoadingText(isDraft ? 'Saving Draft...' : 'Updating Generate Request...');
                const token = localStorage.getItem('accessToken');
                const requestVendors = originalRequestData ? (originalRequestData.RequestVendors || originalRequestData.PurchaseOrderVendors || []) : [];
                const requestItems = originalRequestData ? (originalRequestData.RequestItems || originalRequestData.PurchaseOrderItems || []) : [];

                const payload = {
                    projectId: formData.projectId?.value,
                    employeeId: formData.employeeId?.value,
                    date: formData.date ? new Date(formData.date.getTime() - (formData.date.getTimezoneOffset() * 60000)).toISOString().split('T')[0] : null,
                    tillDate: formData.tillDate ? new Date(formData.tillDate.getTime() - (formData.tillDate.getTimezoneOffset() * 60000)).toISOString().split('T')[0] : null,
                    particular: formData.particular || '',
                    vendors: (formData.vendorId || []).map(v => v.value),
                    items: items.map(it => {
                        const itemObj = {
                            productId: it.productId,
                            productDescription: it.productDescription || it.productName,
                            size: it.size || '',
                            qty: parseFloat(it.qty) || 0,
                            rate: parseFloat(it.rate) || 0,
                            unitId: it.unitId
                        };

                        if (it.reqItemId) {
                            itemObj.reqItemId = parseInt(it.reqItemId, 10);
                        }

                        return itemObj;
                    })
                };

                const response = await axios.put(UPDATE_PURCHASE_ORDER_API(id), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    setLoading(false);

                    // --- Automated WhatsApp Notification on Update ---
                    if (!isDraft) {
                        try {
                            const token = localStorage.getItem('accessToken');
                            // Start Custom WhatsApp Loader
                            setWhatsappLoading(true);
                            const totalVendors = formData.vendorId.length;
                            setWhatsappProgress({ current: 0, total: totalVendors, vendorName: '' });

                            // 1. Selected vendors already contain whatsappNumber from mapResponse
                            const flatVendors = formData.vendorId;

                            const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

                            // 2. Loop through selected vendors and send links
                            for (let i = 0; i < totalVendors; i++) {
                                const vendorSummary = formData.vendorId[i];
                                const fullVendor = flatVendors.find(vo => vo.value === vendorSummary.value);

                                setWhatsappProgress({
                                    current: i + 1,
                                    total: totalVendors,
                                    vendorName: vendorSummary.label
                                });

                                // --- Control Delay between consecutive calls ---
                                if (i > 0) await sleep(2000); // 2 second delay between calls

                                const targetNumber = fullVendor?.whatsappNumber ||
                                    fullVendor?.whatsAppNumber ||
                                    fullVendor?.whatsapp_number ||
                                    fullVendor?.contactNumber ||
                                    fullVendor?.mobileNumber ||
                                    vendorSummary.whatsappNumber || '';

                                if (targetNumber) {
                                    const encryptedRequestId = encryptToken(Number(id));
                                    const encryptedVendorId = encryptToken(Number(vendorSummary.value));
                                    const portalLink = `${window.location.origin}/vendor-portal/${encryptedRequestId}/${encryptedVendorId}`;

                                    const whatsappPayload = {
                                        vendorId: vendorSummary.value,
                                        message: `Please click on this link to update rate for the updated generate request.\n\n${portalLink}`,
                                        link: portalLink
                                    };

                                    try {
                                        const response = await axios.post(SEND_WHATSAPP_LINK_API(id), whatsappPayload, {
                                            headers: { Authorization: `Bearer ${token}` }
                                        });

                                        // ✅ Check for internal failures in response data even if status is 200/true
                                        const resDataArr = response.data?.data;
                                        const hasInternalError = Array.isArray(resDataArr) && resDataArr.length > 0 && resDataArr[0].error;

                                        if (hasInternalError) {
                                            console.error(`Internal WhatsApp failure for ${vendorSummary.label}:`, resDataArr[0].error);
                                        }
                                    } catch (waInnerError) {
                                        console.error(`Network error sending WhatsApp to ${vendorSummary.label}:`, waInnerError);
                                    }
                                }
                            }

                            // ✅ TRIGGER ADMIN NOTIFICATIONS: Exactly once after all vendors are processed.
                            if (totalVendors > 0) {
                                try {
                                    await axios.post(
                                        SEND_WHATSAPP_LINK_API(id),
                                        { message: "ignore_this_message" },
                                        { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                } catch (waAdminError) {
                                    console.error('Failed to send admin notification:', waAdminError);
                                }
                            }

                            setWhatsappLoading(false);
                        } catch (waError) {
                            console.error('Error sending automated WhatsApp on update:', waError);
                            setWhatsappLoading(false);
                        }
                    }

                    Swal.fire({
                        icon: 'success',
                        title: isDraft ? 'Draft Saved!' : 'Success!',
                        text: isDraft ? 'Generate Request saved as draft successfully.' : 'Generate Request updated successfully.',
                        timer: 2000,
                        showConfirmButton: false,
                    });

                    if (!isDraft) {
                        navigate('/generate-requests');
                    }
                } else {
                    showErrorToast(response.data.error || response.data.message || 'Failed to update Request');
                }
            } catch (error) {
                console.error('Error updating Request:', error);
                const errorData = error.response?.data?.error || error.response?.data?.message || 'An error occurred while updating Request';
                showErrorToast(errorData);
            } finally {
                setLoading(false);
            }
        }
    };


    if (loading && loadingText !== 'Saving Draft...') return <Loader fullPage text={loadingText} />;

    return (
        <div className="add-po-page">
            <div className="page-header">
                <button onClick={() => navigate('/generate-requests')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>Update Generate Request</h2>
            </div>

            <form className="project-form" onSubmit={(e) => e.preventDefault()}>
                <div className="form-section">
                    <h3>General Details</h3>
                    <div className="form-grid">

                        <div className="form-group">
                            <label>Project Name / Site Name <span className="required-star">*</span></label>
                            <PaginatedSelect
                                apiUrl={GET_ALL_PROJECT_API}
                                mapResponse={(p) => ({ value: p.id || p._id, label: p.projectName })}
                                value={formData.projectId}
                                onChange={(sel) => handleSelectChange(sel, 'projectId')}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                placeholder="Select project"
                                isClearable
                            />
                        </div>
                        <div className="form-group">
                            <label>Vendor Name <span className="required-star">*</span></label>
                            <Select
                                options={vendorOptions}
                                isMulti
                                value={formData.vendorId}
                                onChange={(sel) => handleSelectChange(sel, 'vendorId')}
                                placeholder={formData.projectId?.value ? "Select vendor(s)" : "First select a project"}
                                isClearable
                                isDisabled={!formData.projectId?.value}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                key={`vendors-${formData.projectId?.value || 'no-project'}`}
                            />
                        </div>
                        <div className="form-group">
                            <label>Order By (Employee) <span className="required-star">*</span></label>
                            <PaginatedSelect
                                apiUrl={GET_ALL_EMPLOYEES_API}
                                mapResponse={(e) => ({
                                    value: e.id || e._id,
                                    label: e.firstName + " " + e.lastName || e.name
                                })}
                                value={formData.employeeId}
                                onChange={(sel) => handleSelectChange(sel, 'employeeId')}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                placeholder="Select employee"
                                isClearable
                            />
                        </div>
                        <div className="form-group">
                            <label>Date <span className="required-star">*</span></label>
                            <DatePicker
                                selected={formData.date}
                                onChange={handleDateChange}
                                dateFormat="yyyy-MM-dd"
                                className="form-control"
                            />
                        </div>
                        <div className="form-group">
                            <label>Date Till</label>
                            <DatePicker
                                selected={formData.tillDate}
                                onChange={(date) => setFormData(prev => ({ ...prev, tillDate: date }))}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                placeholderText="Select validity date"
                            />
                        </div>
                        <div className="form-group full-width">
                            <label>Purpose / Particular</label>
                            <textarea
                                rows={3}
                                name="particular"
                                value={formData.particular}
                                onChange={handleInputChange}
                                placeholder="Enter purpose of order"
                            />
                        </div>
                        {/* <div className="form-group">
                            <label>Status <span className="required-star">*</span></label>
                            <Select
                                options={[
                                    { value: 'pending', label: 'Pending' },
                                    { value: 'approved', label: 'Approved' },
                                    { value: 'completed', label: 'Completed' },
                                    { value: 'cancelled', label: 'Cancelled' }
                                ]}
                                value={{ value: formData.status, label: formData.status.charAt(0).toUpperCase() + formData.status.slice(1) }}
                                onChange={(sel) => setFormData(prev => ({ ...prev, status: sel.value }))}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                placeholder="Select Status"
                                isClearable
                                isLoading={fetchingData}
                            />
                        </div> */}
                    </div>
                </div>

                <div className="form-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h3 style={{ margin: 0 }}>Items List</h3>
                    </div>

                    <div className="item-selection-wrapper">
                        <PaginatedSelect
                            apiUrl={GET_ALL_PRODUCTS_API}
                            mapResponse={(p) => ({
                                value: p.id || p._id,
                                label: p.productName,
                                description: p.description,
                                size: p.size,
                                unitId: p.Unit?.id || p.Unit?._id || p.unitId || '',
                                unitName: p.Unit?.unitName || p.unitName || p.unit || '',
                                rate: p.defaultRate || p.rate
                            })}
                            onChange={handleItemSelect}
                            placeholder="Search & Select Product to Add"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            value={null}
                            isClearable
                        />
                    </div>

                    <div className="items-table-container">
                        <table className="items-table">
                            <thead className="thead-po">
                                <tr>
                                    <th style={{ width: '50px' }}>SR No</th>
                                    <th>Product Name</th>
                                    <th style={{ width: '250px' }}>Product Description</th>
                                    <th style={{ width: '100px' }}>Unit</th>
                                    <th style={{ width: '100px' }}>Size</th>
                                    <th style={{ width: '100px' }}>Qty</th>
                                    <th style={{ width: '120px' }}>Rate</th>
                                    <th style={{ width: '120px' }}>Total</th>
                                    <th style={{ width: '50px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => (
                                    <tr key={item.id}>
                                        <td style={{ textAlign: 'center' }}>{item.srNo}</td>
                                        <td>
                                            <div style={{ fontWeight: '500', color: '#1e293b' }}>
                                                {item.productName || item.productDescription}
                                            </div>
                                        </td>
                                        <td style={{ width: '250px', minWidth: '250px', maxWidth: '250px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                                {item.productDescription}
                                            </div>
                                        </td>
                                        <td>{item.unitName || '-'}</td>
                                        <td>{item.size}</td>
                                        <td>{item.qty}</td>
                                        <td>{item.rate.toLocaleString()}</td>
                                        <td style={{ fontWeight: '600', color: '#4f46e5' }}>
                                            {item.total.toLocaleString()}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    type="button"
                                                    className="item-edit-btn"
                                                    onClick={() => handleEditItem(item)}
                                                    style={{ border: 'none', background: '#f1f5f9', color: '#64748b', padding: '6px', borderRadius: '4px', cursor: 'pointer' }}
                                                >
                                                    <MdEdit size={18} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="item-delete-btn"
                                                    onClick={() => removeItemRow(item.id)}
                                                    style={{ border: 'none', background: '#fee2e2', color: '#ef4444', padding: '6px', borderRadius: '4px', cursor: 'pointer' }}
                                                >
                                                    <MdDelete size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {items.length === 0 && (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                                            No items added yet. Search and select a product above.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="mobile-items-view">
                        {items.length === 0 ? (
                            <div className="no-data">No items added yet.</div>
                        ) : (
                            items.map((item, index) => (
                                <div key={item.id} className="mobile-item-card">
                                    <div className="mobile-item-header">
                                        <div className="mobile-item-title">
                                            <div className="sr-badge">0{index + 1}</div>
                                            <strong>{item.productName || item.productDescription}</strong>
                                        </div>
                                    </div>
                                    <div className="mobile-item-body">
                                        <div className="mobile-item-row full-width">
                                            <span className="m-label">Description</span>
                                            <span className="m-value">{item.productDescription || '-'}</span>
                                        </div>
                                        <div className="mobile-item-row">
                                            <span className="m-label">Qty</span>
                                            <span className="m-value">{item.qty} {item.unitName || 'Nos'}</span>
                                        </div>
                                        <div className="mobile-item-row">
                                            <span className="m-label">Size</span>
                                            <span className="m-value">{item.size || '-'}</span>
                                        </div>
                                        <div className="mobile-item-row">
                                            <span className="m-label">Rate</span>
                                            <span className="m-value">₹{item.rate.toLocaleString()}</span>
                                        </div>
                                        <div className="mobile-item-row">
                                            <span className="m-label">Total</span>
                                            <span className="m-value" style={{ color: '#4f46e5' }}>₹{item.total.toLocaleString()}</span>
                                        </div>
                                    </div>
                                    <div className="mobile-item-actions">
                                        <button type="button" onClick={() => handleEditItem(item)} className="item-edit-btn" style={{ border: 'none', background: '#f1f5f9', color: '#64748b', padding: '6px', borderRadius: '4px', cursor: 'pointer' }}>
                                            <MdEdit size={18} />
                                        </button>
                                        <button type="button" onClick={() => removeItemRow(item.id)} className="item-delete-btn" style={{ border: 'none', background: '#fee2e2', color: '#ef4444', padding: '6px', borderRadius: '4px', cursor: 'pointer' }}>
                                            <MdDelete size={18} />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="po-summary">
                        <div className="grand-total-card">
                            <div className="total-label">Grand Total</div>
                            <div className="total-amount">{grandTotal.toLocaleString()}</div>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/generate-requests')} className="cancel-btn">
                        Cancel
                    </button>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <button
                            type="button"
                            className="save-btn draft-btn"
                            onClick={() => handleUpdate(true)}
                            disabled={loading}
                        >
                            <MdSave /> {loading && loadingText === 'Saving Draft...' ? 'Saving...' : 'Save as Draft'}
                        </button>
                        <button
                            type="button"
                            className="save-btn finalize-btn"
                            onClick={() => handleUpdate(false)}
                            disabled={loading}
                        >
                            <MdSend /> Update Request
                        </button>
                    </div>
                </div>
            </form>

            {/* Item Modal */}
            <Modal
                isOpen={itemModalOpen}
                onClose={() => setItemModalOpen(false)}
                title={isEditingItem ? 'Edit Item' : 'Add Item'}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button className="cancel-btn" onClick={() => setItemModalOpen(false)}>Cancel</button>
                        <button className="save-btn" onClick={handleSaveItem}>{isEditingItem ? 'Update Item' : 'Add to List'}</button>
                    </div>
                }
            >
                <div className="modal-grid">
                    <div className="modal-form-group full-width">
                        <label>Product Name</label>
                        <input
                            value={itemModalData.productName || ''}
                            readOnly
                            style={{ background: '#f1f5f9', color: '#64748b' }}
                        />
                    </div>

                    <div className="modal-form-group full-width">
                        <label>Description</label>
                        <textarea
                            rows="2"
                            value={itemModalData.productDescription || ''}
                            onChange={(e) => handleItemModalChange('productDescription', e.target.value)}
                            placeholder="Product Description"
                        ></textarea>
                    </div>

                    <div className="modal-form-group">
                        <label>Unit</label>
                        <PaginatedSelect
                            apiUrl={GET_ALL_UNITS_API}
                            mapResponse={(u) => ({ value: u.id || u._id, label: u.unitName })}
                            value={itemModalData.unit}
                            onChange={(sel) => handleItemModalChange('unit', sel)}
                            placeholder="Select Unit"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            isClearable
                            menuPosition="fixed"
                        />
                    </div>

                    <div className="modal-form-group">
                        <label>Size</label>
                        <input
                            value={itemModalData.size || ''}
                            onChange={(e) => handleItemModalChange('size', e.target.value)}
                            placeholder="e.g. 10x10"
                        />
                    </div>

                    <div className="modal-form-group">
                        <label>Quantity</label>
                        <input
                            type="number"
                            min="1"
                            value={itemModalData.qty}
                            onChange={(e) => handleItemModalChange('qty', e.target.value)}
                        />
                    </div>

                    <div className="modal-form-group">
                        <label>Rate</label>
                        <input
                            type="number"
                            min="0"
                            value={itemModalData.rate}
                            onChange={(e) => handleItemModalChange('rate', e.target.value)}
                        />
                    </div>

                    <div className="modal-form-group full-width">
                        <label>Total</label>
                        <input
                            type="number"
                            value={itemModalData.total}
                            readOnly
                            style={{ background: '#f1f5f9' }}
                        />
                    </div>
                </div>
            </Modal>

            {/* WhatsApp Notification Loader Modal */}
            {whatsappLoading && (
                <div className="whatsapp-loader-overlay">
                    <div className="whatsapp-loader-card">
                        <div className="whatsapp-icon-wrapper">
                            <FaWhatsapp className="whatsapp-anim-icon" size={50} />
                            <div className="pulse-ring"></div>
                        </div>
                        <h3>Sending Status</h3>
                        <p>Notifying vendors via WhatsApp...</p>
                        <div className="wa-progress-container">
                            <div className="wa-progress-bar">
                                <div
                                    className="wa-progress-fill"
                                    style={{ width: `${(whatsappProgress.current / whatsappProgress.total) * 100}%` }}
                                ></div>
                            </div>
                            <div className="wa-progress-stats">
                                <span>{whatsappProgress.vendorName || 'Initializing...'}</span>
                                <span>{whatsappProgress.current} / {whatsappProgress.total}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditGenerateRequest;
